import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash.isempty';

import useIsUiHidden from 'hooks/dom/useIsUiHidden';
import { CHART_MARGINS } from 'constants/chart';

import YAxisTick from './YAxisTick';
import ChartWrapper from '../ChartWrapper';
import LazyLoader from '../LazyLoader';
import colors from 'constants/colors';

import { ComposedChart, XAxis, YAxis, Tooltip, Legend, Line, CartesianGrid } from 'recharts';
import RatingsTooltip from './RatingsTooltip';

const RatingClassType = {
  _0: 12,
  _1: 1,
  _2: 2,
  _3: 3,
  _4: 4,
  _5: 5,
  _6: 6,
  _7: 7,
  _8: 8,
  _9: 9,
  _10: 10,
  _99: 11,
};

const ScoringHistoryGraph = chartProps => (
  <ChartWrapper {...chartProps}>{props => <Chart {...props}></Chart>}</ChartWrapper>
);

const Chart = props => {
  const {
    defaultData = [],
    bankruptcyData = [],
    defaultRiskClasses = [],
    bankruptcyRiskClasses = [],
    width,
    callbackRef,
  } = props;

  const { t } = useTranslation();

  // isUiHidden is used to check if this is a pdf view or not
  const isUiHidden = useIsUiHidden();

  // making const yAxisWidth instead of reading props
  const yAxisWidth = isUiHidden ? 58 : 80;
  const height = 355;
  const prepareData = (defaultData, bankruptcyData) => {
    if (defaultData?.length && bankruptcyData?.length) {
      return defaultData.map((item, i) => {
        const date = new Date(item.scoreDate);
        const year = date.getFullYear();
        const translatedQuarter = t(`professionalReport.companyRatings.${item.quarter}`);
        return {
          ...item,
          defaultPd: item.pd,
          defaultRatingClass: RatingClassType[item.ratingClass],
          bankruptcyPd: bankruptcyData[i].pd,
          bankruptcyRatingClass: RatingClassType[bankruptcyData[i].ratingClass],
          yearQuarter: item?.quarter ? `${year} ${translatedQuarter}` : t('professionalReport.companyRatings.actual'),
        };
      });
    } else return [];
  };

  const modifiedData = prepareData(defaultData, bankruptcyData);

  const latestRecord = useMemo(() => modifiedData[modifiedData.length - 1] ?? {}, [modifiedData]);

  const series = [
    {
      dataKey: 'defaultRatingClass',
      stroke: 'rgba(153, 153, 153, 1)',
      key: 'defaultRatingClass',
      dataKey2: 'defaultPd',
    },
    {
      dataKey: 'bankruptcyRatingClass',
      stroke: 'rgba(27, 162, 183, 1)',
      key: 'bankruptcyRatingClass',
      dataKey2: 'bankruptcyPd',
    },
  ];

  const valuesWidth = isUiHidden ? 30 : 40;

  return (
    <LazyLoader defaultValue={isUiHidden}>
      {(waypoint, isVisible) => (
        <div>
          {waypoint}
          {_isEmpty(defaultData) && _isEmpty(bankruptcyData) && <div>{t('global.noInformation')}</div>}
          {isVisible && defaultData?.length > 0 && bankruptcyData?.length > 0 && (
            <>
              <div
                className="ratingsHistoryChart no-break"
                style={{ pageBreakInside: 'avoid', pageBreakAfter: 'auto' }}
                ref={callbackRef}
              >
                <ComposedChart
                  id="ratingsHistoryChart"
                  width={isUiHidden ? width - 15 : width - 200}
                  // width={width}
                  height={height}
                  data={modifiedData}
                  margin={{ ...CHART_MARGINS, top: 50 }}
                >
                  {/*gradientAxis - gradient color rating values background */}
                  <defs>
                    <linearGradient id="gradientAxis" gradientTransform="rotate(90)">
                      <stop offset="0%" stopColor="rgb(51, 178, 2)" stopOpacity="1" />
                      <stop offset="50%" stopColor="rgb(255,245,0)" stopOpacity="1" />
                      <stop offset="82%" stopColor="rgb(255, 0, 0)" stopOpacity="1" />
                      <stop offset="100%" stopColor="black" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <rect
                    width={isUiHidden ? 22 : 30}
                    height="285"
                    x={isUiHidden ? yAxisWidth + 10 : yAxisWidth + 35}
                    y={isUiHidden ? 38 : 30}
                    fill="url(#gradientAxis)"
                    rx={5}
                  />

                  <text
                    x={isUiHidden ? yAxisWidth + 40 : yAxisWidth + 55}
                    y={isUiHidden ? CHART_MARGINS.top + 10 : CHART_MARGINS.top}
                    fill="black"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    style={{ fontSize: isUiHidden ? 9 : 12 }}
                  >
                    {t('professionalReport.companyRatings.riskClasses')}
                  </text>

                  <text x={-180} y={20} transform="rotate(-90)" textAnchor="middle" alignmentBaseline="middle">
                    {t('professionalReport.companyRatings.verticalLabel')}
                  </text>

                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    tick={<YAxisTick riskClasses={defaultRiskClasses} isUiHidden={isUiHidden} defaultRatings />}
                    domain={[1, 12]}
                    width={isUiHidden ? 70 : yAxisWidth + 50}
                    tickCount={12}
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                    padding={{ bottom: 15 }}
                    reversed
                  />
                  <rect
                    width={valuesWidth}
                    height="285"
                    x={isUiHidden ? yAxisWidth - 25 : yAxisWidth - 30}
                    y={isUiHidden ? 38 : 30}
                    fill="rgba(51, 51, 51, 0.1)"
                    rx={5}
                  />

                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={<YAxisTick riskClasses={bankruptcyRiskClasses} isUiHidden={isUiHidden} />}
                    domain={[1, 12]}
                    width={5}
                    tickCount={12}
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                    padding={{ bottom: 15 }}
                    reversed
                  />
                  <rect
                    width={valuesWidth}
                    height="285"
                    x={isUiHidden ? width - 155 - yAxisWidth : width - 300 - yAxisWidth}
                    y={isUiHidden ? 38 : 30}
                    fill="rgba(27, 162, 183, 0.2)"
                    rx={5}
                  />

                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="yearQuarter" padding={{ left: isUiHidden ? 20 : 30, right: isUiHidden ? 20 : 30 }} />
                  <Tooltip
                    wrapperStyle={{ visibility: 'visible' }}
                    position={{ x: isUiHidden ? width - 160 : width - 325, y: height * 0.25 }}
                    valueTranslator={(name, ratingValue, pd) => {
                      if (ratingValue > 10) {
                        return (
                          t(`graph.${name}.tooltip.value1`, {
                            ratingValue: ratingValue === 11 ? 99 : ratingValue === 12 ? 0 : ratingValue,
                          }) +
                          ' | ' +
                          t(`professionalReport.companyRatings.description.${ratingValue}`)
                        );
                      } else {
                        return t(`graph.${name}.tooltip.value2`, { ratingValue, pd });
                      }
                    }}
                    nameTranslation={name => t(`graph.ratingsHistoryChart.dataKey.${name}`)}
                    content={<RatingsTooltip latestRecord={latestRecord} series={series} />}
                  />
                  <Line
                    yAxisId="left"
                    dataKey="defaultRatingClass"
                    stroke={'rgba(153, 153, 153, 1)'}
                    isAnimationActive={false}
                    activeDot={{
                      r: 8,
                      fill: colors.COLOR_WHITE,
                      stroke: 'rgba(153, 153, 153, 1)',
                    }}
                    dot={({ cx, cy, stroke, strokeWidth }) => (
                      <circle
                        key={cx}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={colors.COLOR_WHITE}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                      />
                    )}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    dataKey="bankruptcyRatingClass"
                    stroke={'rgba(27, 162, 183, 1)'}
                    isAnimationActive={false}
                    activeDot={{
                      r: 8,
                      fill: colors.COLOR_WHITE,
                      stroke: 'rgba(27, 162, 183, 1)',
                    }}
                    dot={({ cx, cy, stroke, strokeWidth }) => (
                      <circle
                        key={cx}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={colors.COLOR_WHITE}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                      />
                    )}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </div>
            </>
          )}
        </div>
      )}
    </LazyLoader>
  );
};

export default ScoringHistoryGraph;
